// Grading-worksheet → framework Definition import (V1, deterministic).
//
// A job-grading / job-architecture worksheet is a MATRIX: one row per grade
// (e.g. 14 → 1), columns describing each grade (Purpose, Scope, Autonomy,
// Supervision, …) plus a Competencies list. The whole file is ONE framework:
//   • each descriptor column → a weighted evaluation Factor (scoring_type=level)
//   • each grade row         → an anchored Level on every factor (anchor_text=cell)
//   • the Competencies column → the framework's competencies
//   • the grade rows         → the framework's Grades
// Weights are split evenly and normalised to sum to exactly 1.0 (the engine
// requires it). The user can re-weight / drop columns before creating.

export type Row = Record<string, string>;

export interface ParsedCsv {
  headers: string[];
  rows: Row[];
  delimiter: string;
}

// --- CSV parsing (RFC-4180-ish: quoted fields, escaped "", newlines in quotes),
//     with delimiter auto-detection (comma / tab / semicolon). ---
export function parseCsv(text: string): ParsedCsv {
  const clean = text.replace(/^﻿/, ''); // strip BOM
  const delimiter = detectDelimiter(clean);
  const table = parseDelimited(clean, delimiter).filter((r) => r.some((c) => c.trim() !== ''));
  if (table.length === 0) return { headers: [], rows: [], delimiter };
  const headers = table[0].map((h, i) => h.trim() || `Column ${i + 1}`);
  const rows: Row[] = table.slice(1).map((cells) => {
    const r: Row = {};
    headers.forEach((h, i) => (r[h] = (cells[i] ?? '').trim()));
    return r;
  });
  return { headers, rows, delimiter };
}

// --- Excel (.xlsx/.xls) → same {headers, rows} shape as the CSV parser.
//     read-excel-file is dynamic-imported so it's only fetched when an actual
//     spreadsheet is uploaded (keeps it out of the main bundle). ---
export async function parseXlsx(file: File): Promise<ParsedCsv> {
  // read-excel-file exposes only subpath entries; use the browser build.
  const readXlsxFile = (await import('read-excel-file/browser')).default;
  const result: any = await readXlsxFile(file as any);
  // Normalize the return: usually Row[][] (rows of the first sheet), but can be a
  // { rows } object or Sheet[] ({ name, data }) depending on the file/build.
  let raw: any[] = Array.isArray(result) ? result : Array.isArray(result?.rows) ? result.rows : [];
  if (raw.length && !Array.isArray(raw[0]) && Array.isArray(raw[0]?.data)) {
    raw = raw[0].data; // Sheet[] → first sheet's rows
  }
  const table = raw
    .filter((r: any) => Array.isArray(r))
    .map((r: any[]) => r.map((c) => (c == null ? '' : String(c))))
    .filter((r: string[]) => r.some((c) => c.trim() !== ''));
  if (table.length === 0) return { headers: [], rows: [], delimiter: 'xlsx' };
  const headers = table[0].map((h, i) => h.trim() || `Column ${i + 1}`);
  const rows: Row[] = table.slice(1).map((cells) => {
    const r: Row = {};
    headers.forEach((h, i) => (r[h] = (cells[i] ?? '').trim()));
    return r;
  });
  return { headers, rows, delimiter: 'xlsx' };
}

function detectDelimiter(text: string): string {
  const firstLine = text.slice(0, text.indexOf('\n') === -1 ? text.length : text.indexOf('\n'));
  const counts: Record<string, number> = { ',': 0, '\t': 0, ';': 0 };
  let inQ = false;
  for (const ch of firstLine) {
    if (ch === '"') inQ = !inQ;
    else if (!inQ && ch in counts) counts[ch]++;
  }
  return (Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[1] ?? 0) > 0
    ? Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0]
    : ',';
}

function parseDelimited(text: string, delim: string): string[][] {
  const rows: string[][] = [];
  let cell = '';
  let row: string[] = [];
  let inQ = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQ) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          cell += '"';
          i++;
        } else inQ = false;
      } else cell += ch;
    } else if (ch === '"') inQ = true;
    else if (ch === delim) {
      row.push(cell);
      cell = '';
    } else if (ch === '\n') {
      row.push(cell);
      rows.push(row);
      row = [];
      cell = '';
    } else if (ch !== '\r') cell += ch;
  }
  if (cell !== '' || row.length) {
    row.push(cell);
    rows.push(row);
  }
  return rows;
}

// --- Column-role auto-detection --------------------------------------------
export interface ColumnMapping {
  gradeCol: string; // grade code
  labelCol: string; // grade label / titles ('' → use "Grade <code>")
  competencyCol: string; // '' → no competencies
  factorCols: string[]; // descriptor columns → evaluation factors
}

const rx = (re: RegExp) => (h: string) => re.test(h);
export function guessMapping(headers: string[]): ColumnMapping {
  const find = (pred: (h: string) => boolean) => headers.find(pred) ?? '';
  const gradeCol = find(rx(/^\s*grade\b/i)) || find(rx(/\bgrade\b/i));
  const levelCol = find((h) => /^\s*level\s*$/i.test(h)); // numeric duplicate of grade
  const labelCol = find(rx(/title|role|position|job\s*title|universal/i));
  const competencyCol = find(rx(/competenc/i));
  const excluded = new Set([gradeCol, levelCol, labelCol, competencyCol].filter(Boolean));
  const factorCols = headers.filter((h) => !excluded.has(h));
  return { gradeCol, labelCol, competencyCol, factorCols };
}

// --- Definition assembly ----------------------------------------------------
const slug = (s: string) =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 48) || 'x';

function uniqueKeys(labels: string[]): string[] {
  const seen = new Map<string, number>();
  return labels.map((l) => {
    const base = slug(l);
    const n = seen.get(base) ?? 0;
    seen.set(base, n + 1);
    return n === 0 ? base : `${base}_${n + 1}`;
  });
}

// evenly-split weights that sum to EXACTLY 1.0 (engine validates approxOne).
function evenWeights(n: number): number[] {
  if (n <= 0) return [];
  const w = Math.round((1 / n) * 1e6) / 1e6;
  const arr = Array(n).fill(w);
  const drift = Math.round((1 - w * n) * 1e6) / 1e6;
  arr[n - 1] = Math.round((arr[n - 1] + drift) * 1e6) / 1e6;
  return arr;
}

export interface BuildResult {
  definition: any;
  summary: { grades: number; factors: number; competencies: number };
  warnings: string[];
}

export function buildDefinition(rows: Row[], m: ColumnMapping): BuildResult {
  const warnings: string[] = [];
  const gradeRows = rows.filter((r) => (m.gradeCol ? r[m.gradeCol]?.trim() : true));

  // Grades (dedupe by code, keep order). score = numeric grade if parseable, else index.
  const grades: any[] = [];
  const gradeScore = new Map<string, number>();
  gradeRows.forEach((r, i) => {
    const code = (m.gradeCol ? r[m.gradeCol] : String(i + 1)).trim() || String(i + 1);
    if (grades.some((g) => g.code === code)) return;
    const num = Number(String(code).replace(/[^\d.]/g, ''));
    const score = Number.isFinite(num) && num > 0 ? num : gradeRows.length - i;
    const label = (m.labelCol && r[m.labelCol]?.trim()) || `Grade ${code}`;
    grades.push({ code, label, order: grades.length + 1 });
    gradeScore.set(code, score);
  });
  if (grades.length === 0) warnings.push('No grades detected — check the Grade column.');

  // Factors: one per descriptor column; a Level per grade (anchor = the cell).
  const factorKeys = uniqueKeys(m.factorCols);
  const weights = evenWeights(m.factorCols.length);
  const maxScore = Math.max(1, ...grades.map((g) => gradeScore.get(g.code) ?? 1));
  const factors = m.factorCols.map((col, idx) => {
    const levels = gradeRows
      .map((r) => {
        const code = (m.gradeCol ? r[m.gradeCol] : '').trim();
        return {
          key: slug(code || col),
          label: (m.labelCol && r[m.labelCol]?.trim()) || `Grade ${code}`,
          score: gradeScore.get(code) ?? 1,
          anchor_text: (r[col] ?? '').trim(),
        };
      })
      .filter((l, i, arr) => arr.findIndex((x) => x.key === l.key) === i);
    return {
      key: factorKeys[idx],
      label: col,
      weight: weights[idx],
      scoring_type: 'level',
      levels: levels.length ? levels : [{ key: 'default', label: 'Default', score: 1, anchor_text: '' }],
    };
  });
  if (factors.length === 0) warnings.push('No factor columns selected — a framework needs at least one.');

  // Competencies (unique, from the competency column; split on ; or ,).
  const compSet = new Map<string, string>();
  if (m.competencyCol) {
    for (const r of gradeRows) {
      for (const c of (r[m.competencyCol] || '').split(/[;,]/)) {
        const t = c.trim().replace(/\s+/g, ' ');
        if (t.length > 1 && t.length < 80) compSet.set(slug(t), t);
      }
    }
  }
  const competencies = Array.from(compSet.entries()).map(([key, label]) => ({ key, label, levels: [] }));

  const definition = {
    factors,
    competencies,
    grades,
    job_families: [],
    salary_bands: [],
    scoring: { type: 'weighted_factors', scale: { min: 0, max: maxScore }, aggregation: 'weighted_average' },
    formulas: [],
    rule_sets: {},
    subject_fields: [],
  };
  return {
    definition,
    summary: { grades: grades.length, factors: factors.length, competencies: competencies.length },
    warnings,
  };
}
